import React, { useState } from "react";
import { useMutation } from "@apollo/client";

import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { moovsBlue } from "design-system/colors";
import { SUBMIT_ISSUE_REPORT_MUTATION } from "globals/graphql/issueReport.graphql";
import { useSnackbar } from "globals/hooks";
import { useLocation } from "react-router-dom";
import MoovsDialog from "components/MoovsDialog";

// Category options
const CATEGORIES = [
  "Flight Aware",
  "SMS Delivery",
  "Email Delivery",
  "Other",
] as const;

type Category = typeof CATEGORIES[number];

// Priority options
const PRIORITIES = ["Low", "Medium", "High"] as const;

type Priority = typeof PRIORITIES[number];

type IssueReportModalProps = {
  open: boolean;
  onClose: () => void;
};

function IssueReportModal(props: IssueReportModalProps) {
  const { open, onClose } = props;

  // State
  const [category, setCategory] = useState<Category | "">("");
  const [priority, setPriority] = useState<Priority>("Medium");
  const [expectedBehavior, setExpectedBehavior] = useState("");
  const [actualBehavior, setActualBehavior] = useState("");
  const [reproductionSteps, setReproductionSteps] = useState("");
  
  // Error states
  const [categoryError, setCategoryError] = useState(false);
  const [expectedBehaviorError, setExpectedBehaviorError] = useState(false);
  const [actualBehaviorError, setActualBehaviorError] = useState(false);
  const [reproductionStepsError, setReproductionStepsError] = useState(false);

  // Hooks
  const snackbar = useSnackbar();
  const { pathname } = useLocation();
  // Mutations
  const [submitIssueReport, { loading }] = useMutation(
    SUBMIT_ISSUE_REPORT_MUTATION,
    {
      onCompleted() {
        snackbar.success("Issue report submitted successfully");
        handleClose();
      },
      onError() {
        snackbar.error("Error submitting issue report");
      },
    }
  );

  // Event handlers
  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value as Category);
    setCategoryError(false);
  };

  const handlePriorityChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPriority: Priority | null
  ) => {
    if (newPriority !== null) {
      setPriority(newPriority);
    }
  };

  const handleExpectedBehaviorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpectedBehavior(e.target.value);
    setExpectedBehaviorError(false);
  };

  const handleActualBehaviorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setActualBehavior(e.target.value);
    setActualBehaviorError(false);
  };

  const handleReproductionStepsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReproductionSteps(e.target.value);
    setReproductionStepsError(false);
  };

  const handleSubmit = () => {
    // Validate inputs
    let hasError = false;
    
    if (!category) {
      setCategoryError(true);
      hasError = true;
    }
    
    if (!expectedBehavior.trim()) {
      setExpectedBehaviorError(true);
      hasError = true;
    }
    
    if (!actualBehavior.trim()) {
      setActualBehaviorError(true);
      hasError = true;
    }
    
    if (!reproductionSteps.trim()) {
      setReproductionStepsError(true);
      hasError = true;
    }
    
    if (hasError) return;

    // Submit issue report
    submitIssueReport({
      variables: {
        input: {
          category,
          priority,
          expectedBehavior,
          actualBehavior,
          reproductionSteps,
          path: pathname,
        },
      },
    });
  };

  const handleClose = () => {
    setCategory("");
    setPriority("Medium");
    setExpectedBehavior("");
    setActualBehavior("");
    setReproductionSteps("");
    
    // Reset errors
    setCategoryError(false);
    setExpectedBehaviorError(false);
    setActualBehaviorError(false);
    setReproductionStepsError(false);
    
    onClose();
  };

  return (
    <MoovsDialog
      open={open}
      onClose={handleClose}
      dialogTitle="Report an Issue"
      onAccept={handleSubmit}
      acceptButtonText="Submit Report"
      closeButtonText="Cancel"
      acceptDisabled={loading}
      size="md"
    >
      <Box pt={2}>
        {/* Category dropdown */}
        <FormControl 
          fullWidth 
          error={categoryError} 
          sx={{ mb: 3 }}
        >
          <InputLabel>Category</InputLabel>
          <Select
            value={category}
            onChange={handleCategoryChange}
            label="Category"
          >
            {CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
          {categoryError && (
            <FormHelperText>Please select a category</FormHelperText>
          )}
        </FormControl>

        {/* Priority toggle buttons */}
        <Box mb={3}>
          <Typography variant="body2" mb={1}>
            Priority
          </Typography>
          <ToggleButtonGroup
            value={priority}
            exclusive
            onChange={handlePriorityChange}
            aria-label="priority"
            fullWidth
            sx={{
              "& .Mui-selected": {
                color: `${moovsBlue} !important`,
              }
            }}
          >
            <ToggleButton value="Low" aria-label="low priority">
              Low
            </ToggleButton>
            <ToggleButton value="Medium" aria-label="medium priority">
              Medium
            </ToggleButton>
            <ToggleButton value="High" aria-label="high priority">
              High
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Expected behavior */}
        <TextField
          fullWidth
          label="Expected Behavior"
          value={expectedBehavior}
          onChange={handleExpectedBehaviorChange}
          error={expectedBehaviorError}
          helperText={expectedBehaviorError ? "Expected behavior is required" : ""}
          sx={{ mb: 3 }}
          placeholder="What should have happened?"
          multiline
          rows={2}
        />

        {/* Actual behavior */}
        <TextField
          fullWidth
          label="Actual Behavior"
          value={actualBehavior}
          onChange={handleActualBehaviorChange}
          error={actualBehaviorError}
          helperText={actualBehaviorError ? "Actual behavior is required" : ""}
          sx={{ mb: 3 }}
          placeholder="What actually happened?"
          multiline
          rows={2}
        />

        {/* Reproduction steps */}
        <TextField
          fullWidth
          label="Steps to Reproduce"
          value={reproductionSteps}
          onChange={handleReproductionStepsChange}
          error={reproductionStepsError}
          helperText={reproductionStepsError ? "Reproduction steps are required" : ""}
          multiline
          rows={5}
          placeholder="1. Go to... 2. Click on... 3. Observe..."
        />
      </Box>
    </MoovsDialog>
  );
}

export default IssueReportModal;
